import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { setZona } from "@/lib/zona";
import { api } from "@/lib/api";
import { DEMO } from "@/mock/data";
import { useAuth } from "@/context/AuthContext";

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

export function DataProvider({ children }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [matters, setMatters] = useState([]);
  const [pets, setPets] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [viviendas, setViviendas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [gastos, setGastos] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [pushPrefs, setPushPrefsState] = useState({});

  const fetchAll = useCallback(async () => {
    const [a, m, f, d, r, ct, v, vh, gs, ing] = await Promise.all([
      api.get("/asuntos"),
      api.get("/mascotas"),
      api.get("/familiares"),
      api.get("/documentos"),
      api.get("/recordatorios"),
      api.get("/contactos"),
      api.get("/viviendas"),
      api.get("/vehiculos"),
      api.get("/gastos"),
      api.get("/ingresos"),
    ]);
    setGastos(gs.data);
    setIngresos(ing.data);
    setMatters(a.data);
    setPets(m.data);
    setFamilyMembers(f.data);
    setDocuments(d.data);
    setReminders(r.data);
    setContactos(ct.data);
    setViviendas(v.data);
    setVehiculos(vh.data);
    return a.data;
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        let asuntos = (await api.get("/asuntos")).data;
        // Migración de datos demo: solo para la cuenta de demostración y una sola vez.
        if (user?.es_demo && asuntos.length === 0) {
          await api.post("/migrar-demo", DEMO);
        }
        if (!active) return;
        try { const r = await api.get("/push/preferencias"); setZona(r.data.timezone); if (active) setPushPrefsState(r.data); } catch { /* sin preferencias: zona por defecto */ }
        await fetchAll();
      } catch (e) {
        console.error("Error cargando datos", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user, fetchAll]);

  // Recalcular estados con el paso del tiempo (cambio de hora/día) y al volver a la app.
  useEffect(() => {
    if (!user) return undefined;
    const tick = () => { if (document.visibilityState === "visible") api.get("/asuntos").then((r) => setMatters(r.data)).catch(() => {}); };
    const id = setInterval(tick, 60000);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [user]);

  const setPushPrefs = async (patch) => {
    const { data } = await api.patch("/push/preferencias", patch);
    setZona(data.timezone);
    setPushPrefsState(data);
    return data;
  };
  const createFamiliar = async (payload) => {
    const { data } = await api.post("/familiares", payload);
    setFamilyMembers((await api.get("/familiares")).data);
    return data;
  };
  const updateFamiliar = async (id, payload) => {
    await api.patch(`/familiares/${id}`, payload);
    setFamilyMembers((await api.get("/familiares")).data);
  };
  const deleteFamiliar = async (id, reasignarTitular = false) => {
    await api.delete(`/familiares/${id}${reasignarTitular ? "?reasignar=titular" : ""}`);
    await fetchAll();
  };

  const createMascota = async (payload) => {
    const { data } = await api.post("/mascotas", payload);
    setPets((await api.get("/mascotas")).data);
    return data;
  };
  const updateMascota = async (id, payload) => {
    await api.patch(`/mascotas/${id}`, payload);
    setPets((await api.get("/mascotas")).data);
  };
  const deleteMascota = async (id) => {
    await api.delete(`/mascotas/${id}`);
    setPets((await api.get("/mascotas")).data);
  };
  const getDocumentosSugeridosMascota = async (petId) => {
    const { data } = await api.get(`/mascotas/${petId}/documentos-sugeridos`);
    return data.documentos;
  };
  const vincularDocumentoContacto = async (docId, contactoId) => {
    await api.post(`/documentos/${docId}/vincular-contacto`, { contacto_id: contactoId });
    setDocuments((await api.get("/documentos")).data);
  };
  const vincularDocumentoMascota = async (docId, petId) => {
    await api.post(`/documentos/${docId}/vincular-mascota`, { pet_id: petId });
    const [p, d, a] = await Promise.all([
      api.get("/mascotas"), api.get("/documentos"), api.get("/asuntos"),
    ]);
    setPets(p.data);
    setDocuments(d.data);
    setMatters(a.data);
  };
  const renombrarDocumento = async (docId, filename) => {
    await api.patch(`/documentos/${docId}/nombre`, { filename });
    setDocuments((await api.get("/documentos")).data);
  };
  const vincularDocumentoPersona = async (docId, fmId, category) => {
    await api.post(`/documentos/${docId}/vincular-persona`, { family_member_id: fmId, category });
    const [d, a] = await Promise.all([api.get("/documentos"), api.get("/asuntos")]);
    setDocuments(d.data); setMatters(a.data);
  };
  const actualizarMascotaDesdeDocumento = async (petId, payload) => {
    const { data } = await api.post(`/mascotas/${petId}/actualizar-desde-documento`, payload);
    setPets((await api.get("/mascotas")).data);
    return data;
  };

  // Viviendas (FASE 6 · Hogar)
  const createVivienda = async (payload) => {
    const { data } = await api.post("/viviendas", payload);
    setViviendas((await api.get("/viviendas")).data);
    return data;
  };
  const updateVivienda = async (id, payload) => {
    await api.patch(`/viviendas/${id}`, payload);
    setViviendas((await api.get("/viviendas")).data);
  };
  const deleteVivienda = async (id) => {
    await api.delete(`/viviendas/${id}`);
    const [v, d, a] = await Promise.all([api.get("/viviendas"), api.get("/documentos"), api.get("/asuntos")]);
    setViviendas(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const getDocumentosSugeridosVivienda = async (vid) => {
    const { data } = await api.get(`/viviendas/${vid}/documentos-sugeridos`);
    return data.documentos;
  };
  const vincularDocumentoVivienda = async (docId, vid) => {
    await api.post(`/documentos/${docId}/vincular-vivienda`, { vivienda_id: vid });
    const [v, d, a] = await Promise.all([api.get("/viviendas"), api.get("/documentos"), api.get("/asuntos")]);
    setViviendas(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const asignarAsuntoAVivienda = async (mid, vid) => {
    await api.patch(`/asuntos/${mid}/vivienda`, { vivienda_id: vid });
    const [v, d, a] = await Promise.all([api.get("/viviendas"), api.get("/documentos"), api.get("/asuntos")]);
    setViviendas(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const recargarHogar = async () => {
    const [d, a] = await Promise.all([api.get("/documentos"), api.get("/asuntos")]);
    setDocuments(d.data); setMatters(a.data);
  };
  const marcarViviendaPendiente = async (docId) => { await api.post(`/documentos/${docId}/vivienda-pendiente`); await recargarHogar(); };
  const viviendaSinAsignar = async (ref) => { await api.post("/vivienda-pendiente/sin-asignar", ref); await recargarHogar(); };
  const actualizarViviendaDesdeDocumento = async (vid, payload) => {
    const { data } = await api.post(`/viviendas/${vid}/actualizar-desde-documento`, payload);
    setViviendas((await api.get("/viviendas")).data);
    return data;
  };
  // Vehículos (FASE 1)
  const createVehiculo = async (payload) => {
    const { data } = await api.post("/vehiculos", payload);
    setVehiculos((await api.get("/vehiculos")).data);
    return data;
  };
  const updateVehiculo = async (id, payload) => {
    await api.patch(`/vehiculos/${id}`, payload);
    setVehiculos((await api.get("/vehiculos")).data);
  };
  const deleteVehiculo = async (id) => {
    await api.delete(`/vehiculos/${id}`);
    const [v, d, a] = await Promise.all([api.get("/vehiculos"), api.get("/documentos"), api.get("/asuntos")]);
    setVehiculos(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const vincularDocumentoVehiculo = async (docId, vid) => {
    await api.post(`/documentos/${docId}/vincular-vehiculo`, { vehiculo_id: vid });
    const [v, d, a] = await Promise.all([api.get("/vehiculos"), api.get("/documentos"), api.get("/asuntos")]);
    setVehiculos(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const asignarAsuntoAVehiculo = async (mid, vid) => {
    await api.patch(`/asuntos/${mid}/vehiculo`, { vehiculo_id: vid });
    const [v, d, a] = await Promise.all([api.get("/vehiculos"), api.get("/documentos"), api.get("/asuntos")]);
    setVehiculos(v.data); setDocuments(d.data); setMatters(a.data);
  };
  const getVehiculoById = (id) => vehiculos.find((v) => v.id === id);
  const getMattersByVehiculo = (vid) => matters.filter((m) => m.vehiculo_id === vid);
  const getDocumentsForVehiculo = (vid) => documents.filter((d) => d.vehiculo_id === vid);

  // Re-análisis de un documento (para vincular documentos antiguos sin bloquear)
  const reanalizarDocumento = async (docId) => {
    const { data } = await api.post(`/documentos/${docId}/reanalizar`);
    return data.analisis;
  };

  // Documentos (FASE 3)
  const recargarGastos = async () => {
    const [g, d] = await Promise.all([api.get("/gastos"), api.get("/documentos")]);
    setGastos(g.data); setDocuments(d.data);
  };
  const leerGasto = async (docId) => (await api.post(`/gastos/leer/${docId}`)).data.datos;
  const guardarGasto = async (id, payload) => {
    const { data } = id ? await api.patch(`/gastos/${id}`, payload) : await api.post("/gastos", payload);
    await recargarGastos();
    return data;
  };
  const borrarGasto = async (id, conDocumento) => {
    await api.delete(`/gastos/${id}`, { params: { documento: !!conDocumento } });
    await recargarGastos();
  };
  const recargarIngresos = async () => setIngresos((await api.get("/ingresos")).data);
  const guardarIngreso = async (id, payload) => {
    const { data } = id ? await api.patch(`/ingresos/${id}`, payload) : await api.post("/ingresos", payload);
    await recargarIngresos();
    return data;
  };
  const borrarIngreso = async (id) => {
    await api.delete(`/ingresos/${id}`);
    await recargarIngresos();
  };
  const subirDocumento = async (file, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    const { data } = await api.post("/documentos/subir", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (extra.sin_ia === "si") setDocuments((await api.get("/documentos")).data);
    return data;
  };
  const analizarDocumento = async (docId) => {
    const { data } = await api.post(`/documentos/${docId}/analizar`);
    return data;
  };
  const confirmarDocumento = async (docId, payload) => {
    const { data } = await api.post(`/documentos/${docId}/confirmar`, payload);
    const [a, d] = await Promise.all([api.get("/asuntos"), api.get("/documentos")]);
    setMatters(a.data);
    setDocuments(d.data);
    return data;
  };
  const eliminarDocumento = async (docId, total = false) => {
    await api.delete(`/documentos/${docId}`, { params: total ? { total: true } : {} });
    const [a, d] = await Promise.all([api.get("/asuntos"), api.get("/documentos")]);
    setMatters(a.data);
    setDocuments(d.data);
  };
  const restaurarDocumento = async (docId) => {
    await api.post(`/documentos/${docId}/restaurar`);
    const [a, d] = await Promise.all([api.get("/asuntos"), api.get("/documentos")]);
    setMatters(a.data);
    setDocuments(d.data);
  };
  const getPapelera = async () => {
    const { data } = await api.get("/documentos/papelera");
    return data;
  };
  const borrarDocumentoDefinitivo = async (docId) => {
    await api.delete(`/documentos/${docId}/definitivo`);
  };
  const detectarDuplicados = async (docId) => {
    const { data } = await api.post(`/documentos/${docId}/duplicados`);
    return data;
  };
  const getDocumentoBlobUrl = async (docId) => {
    const resp = await api.get(`/documentos/${docId}/archivo`, { responseType: "blob" });
    return URL.createObjectURL(resp.data);
  };

  // Asuntos manuales, recordatorios y avisos por email (FASE 3)
  const refreshAsuntos = async () => {
    const a = await api.get("/asuntos");
    setMatters(a.data);
    return a.data;
  };
  const crearAsunto = async (payload) => {
    const { data } = await api.post("/asuntos", payload);
    await refreshAsuntos();
    return data;
  };
  const cierreAsunto = async (id, body) => {
    const { data } = await api.post(`/asuntos/${id}/cierre`, body);
    await refreshAsuntos();
    return data;
  };
  const eliminarAsunto = async (id) => {
    await api.delete(`/asuntos/${id}`);
    const [a, r] = await Promise.all([api.get("/asuntos"), api.get("/recordatorios")]);
    setMatters(a.data);
    setReminders(r.data);
  };
  const crearRecordatorio = async (payload) => {
    await api.post("/recordatorios", payload);
    await refreshAsuntos();
  };
  const actualizarRecordatorio = async (id, payload) => {
    await api.patch(`/recordatorios/${id}`, payload);
    await refreshAsuntos();
    setReminders((await api.get("/recordatorios")).data);
  };
  const eliminarRecordatorio = async (id) => {
    await api.delete(`/recordatorios/${id}`);
    await refreshAsuntos();
    setReminders((await api.get("/recordatorios")).data);
  };
  const editarAsunto = async (asuntoId, payload) => {
    const { data } = await api.patch(`/asuntos/${asuntoId}/datos`, payload);
    await refreshAsuntos();
    return data;
  };
  const guardarRecordatorioPuntual = async (asuntoId, payload) => {
    const { data } = asuntoId ? await api.put(`/asuntos/${asuntoId}/recordatorio`, payload) : await api.post("/asuntos/recordatorio", payload);
    await refreshAsuntos();
    setReminders((await api.get("/recordatorios")).data);
    return data;
  };
  const guardarCorreoRecibido = async (fd) => {
    const { data } = await api.post("/correos-recibidos", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (fd.get("sin_ia") === "si") setDocuments((await api.get("/documentos")).data);
    return data;
  };
  const actualizarAvisos = async (asuntoId, payload) => {
    const { data } = await api.patch(`/asuntos/${asuntoId}/avisos`, payload);
    await refreshAsuntos();
    return data;
  };
  const agenteChat = async (session_id, message) => {
    const { data } = await api.post("/agente/chat", { session_id, message });
    return data;
  };
  const agenteConversaciones = async () => (await api.get("/agente/conversaciones")).data;
  const agenteMensajes = async (cid) => (await api.get(`/agente/conversaciones/${cid}/mensajes`)).data;
  const agenteBorrarConversacion = async (cid) => { await api.delete(`/agente/conversaciones/${cid}`); };

  // Avisos proactivos (FASE 4)
  const getAvisosProactivos = async () => (await api.get("/avisos-proactivos")).data;
  const avisoVisto = async (id) => { await api.post("/avisos-proactivos/visto", { id }); };
  const avisoPosponer = async (id, dias = 1) => { await api.post("/avisos-proactivos/posponer", { id, dias }); };
  const avisoTipo = async (tipo, activo) => { await api.post("/avisos-proactivos/tipo", { tipo, activo }); };

  // Resumen semanal por email (FASE 4)
  const getResumenPref = async () => (await api.get("/resumen-semanal/preferencia")).data;
  const setResumenPref = async (payload) => (await api.post("/resumen-semanal/preferencia", payload)).data;
  const getResumenPreview = async () => (await api.get("/resumen-semanal/preview")).data;

  // Correos (FASE 5) — envío gestionado solo al propio usuario; mailto para terceros
  const enviarCorreoAMi = async (payload) => (await api.post("/correos/enviar-a-mi", payload)).data;
  const registrarCorreo = async (payload) => (await api.post("/correos/registro", payload)).data;
  const listarCorreos = async () => (await api.get("/correos")).data;

  // Agenda de contactos (FASE 5)
  const refreshContactos = async () => { setContactos((await api.get("/contactos")).data); };
  const crearContacto = async (payload) => { const { data } = await api.post("/contactos", payload); await refreshContactos(); return data; };
  const actualizarContacto = async (id, payload) => { await api.patch(`/contactos/${id}`, payload); await refreshContactos(); };
  const eliminarContacto = async (id) => { await api.delete(`/contactos/${id}`); await refreshContactos(); };

  // Recordar seguimiento (FASE 5)
  const refreshReminders = async () => { setReminders((await api.get("/recordatorios")).data); };
  const crearSeguimiento = async (payload) => {
    const { data } = await api.post("/correos/seguimiento", payload);
    await refreshReminders();
    return data;
  };

  // Selectores (equivalentes a los helpers de FASE 1, ahora sobre datos reales)
  const getMatterById = (id) => matters.find((m) => m.id === id);
  const getMattersByMember = (fmId) => matters.filter((m) => m.family_member_id === fmId);
  const getMattersByPet = (petId) => matters.filter((m) => m.pet_id === petId);
  const getPetById = (id) => pets.find((p) => p.id === id);
  const getDocumentsForMatter = (matter) =>
    documents.filter((d) => (matter?.documents || []).includes(d.id));
  const getDocumentsForPet = (petId) => documents.filter((d) => d.pet_id === petId);
  const getMemberById = (id) => familyMembers.find((m) => m.id === id);
  const getViviendaById = (id) => viviendas.find((v) => v.id === id);
  const getMattersByVivienda = (vid) => matters.filter((m) => m.vivienda_id === vid);
  const getDocumentsForVivienda = (vid) => documents.filter((d) => d.vivienda_id === vid);
  const getCategoryCount = (cat) => {
    if (cat.id === "familia") return familyMembers.filter((f) => !f.es_titular && f.id !== "fm-self").length;
    if (cat.id === "mascotas") return pets.length;
    if (cat.id === "viviendas") return viviendas.length;
    if (cat.id === "vehiculos") return vehiculos.length;
    return matters.filter((m) => m.category === cat.match).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center gap-4">
        <div className="w-14 h-14 rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
        <p className="text-base text-[#6E7A74]">Cargando tus asuntos…</p>
      </div>
    );
  }

  return (
    <DataContext.Provider
      value={{ fetchAll,
        matters, pets, familyMembers, documents, reminders, contactos, viviendas, vehiculos,
        pushPrefs, setPushPrefs, refetch: fetchAll,
        createFamiliar, updateFamiliar, deleteFamiliar,
        createMascota, updateMascota, deleteMascota,
        getDocumentosSugeridosMascota, vincularDocumentoMascota, vincularDocumentoContacto, recargarHogar, vincularDocumentoPersona, renombrarDocumento, actualizarMascotaDesdeDocumento,
        createVivienda, updateVivienda, deleteVivienda,
        getDocumentosSugeridosVivienda, vincularDocumentoVivienda, actualizarViviendaDesdeDocumento, asignarAsuntoAVivienda, marcarViviendaPendiente, viviendaSinAsignar,
        reanalizarDocumento,
        gastos, leerGasto, guardarGasto, borrarGasto, ingresos, guardarIngreso, borrarIngreso,
        subirDocumento, analizarDocumento, confirmarDocumento, eliminarDocumento, restaurarDocumento, getPapelera, borrarDocumentoDefinitivo, detectarDuplicados, getDocumentoBlobUrl,
        crearAsunto, cierreAsunto, eliminarAsunto, crearRecordatorio, actualizarRecordatorio, eliminarRecordatorio, actualizarAvisos, editarAsunto, guardarRecordatorioPuntual, guardarCorreoRecibido, agenteChat,
        agenteConversaciones, agenteMensajes, agenteBorrarConversacion,
        getAvisosProactivos, avisoVisto, avisoPosponer, avisoTipo,
        getResumenPref, setResumenPref, getResumenPreview,
        enviarCorreoAMi, registrarCorreo, listarCorreos,
        crearContacto, actualizarContacto, eliminarContacto,
        crearSeguimiento,
        getMatterById, getMattersByMember, getMattersByPet, getPetById,
        getDocumentsForMatter, getDocumentsForPet, getMemberById, getCategoryCount,
        getViviendaById, getMattersByVivienda, getDocumentsForVivienda,
        createVehiculo, updateVehiculo, deleteVehiculo, vincularDocumentoVehiculo, asignarAsuntoAVehiculo,
        getVehiculoById, getMattersByVehiculo, getDocumentsForVehiculo,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}
